import axios, { AxiosResponse } from 'axios';
import { errorBus } from '../errorHandle';
import { GIFT_CLAIM_CODES, GIFT_LINK_QUOTA_INVALID, isGiftClaimUrl } from './giftCodes';

// Single generic fallback for errors with no usable backend message:
// network failure, timeout, 5xx with no body, or a structured error missing `message`.
const GENERIC_ERROR_MESSAGE = "Ocorreu um erro. Tente novamente.";

// The backend (octofort_api) now returns localized BR-PT copy in `message` for
// every APIErrorCode. Pass it through unchanged instead of overriding it with a
// hardcoded English dictionary — the backend is the single source of truth.
export const getTemplateError = (response: any) => {
  const message = typeof response?.message === "string" ? response.message : "";

  if (message) {
    return {
      code: response.code,
      message: message
    }
  } else {
    return false;
  }
}

// Use proxy in development, direct URL in production
const getBaseURL = () => {
  if (process.env.NODE_ENV === 'development') {
    // In development, use relative URLs which will be proxied
    return '';
  }
  // In production, use the direct server URL
  return '';
};

const baseURL = getBaseURL();
console.log('API Client configured with baseURL:', baseURL);

const apiClient = axios.create({
  baseURL: baseURL,
  timeout: 10000,
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken'
});

// 统一错误拦截器
apiClient.interceptors.response.use(
  (response: AxiosResponse): any => {
    try {
      if (response.status === 204) {
        return {
          data: null,
          message: "",
          code: 0
        };
      }
      // Flat-response fallback (Bundle 5b): some endpoints return {field: value}
      // instead of {data: {...}, code: 0}. Use data.data if present, else data itself.
      const payload = response.data?.data ?? response.data;
      return {
        data: payload,
        message: response.data?.message ?? "",
        code: (response.data.code !== undefined && response.data.code !== null) ? response.data.code : response.status
      }
    } catch (error) {
      return {
        data: error,
        message: "",

        code: response.status
      }
    }
  },

  error => {

    // Handle CORS errors specifically
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      console.error('CORS or Network error detected:', error);
      const corsResponse = {
        data: null,
        message: GENERIC_ERROR_MESSAGE,
        code: 999 // Don't trigger error notification for CORS issues
      };
      return corsResponse;
    }
    //hook各自处理 已知错误 code = 1000+
    //hook各自处理 api校验错误 code = 4**,{key1:[msg1,msg2]}
    let message = "";
    if (error.response?.status === 404) {
      const notFound = {
        data: null,
        message: GENERIC_ERROR_MESSAGE,
        code: 404
      }
      // Suppress toast where the page renders its own friendly state:
      //   /api/draw/<id>/                          — BetWrapper shows closed-draw message
      //   /api/affiliate/onboarding/               — invite page shows invalid-token state
      //   /api/affiliate/application/<id>/status/  — applicant status page shows not-found state
      //   /api/gift/claim/<token>/                 — claim page shows not-found state. Also
      //     covers the deploy window in which the gifting app is not yet live and Django
      //     404s the URL itself: a shared link then dead-ends quietly instead of
      //     stacking a red snackbar on top of the page's own message.
      const url = error.config?.url || '';
      if (!url.match(/\/api\/draw\/\d+\/|\/api\/affiliate\/onboarding\/|\/api\/affiliate\/application\/[^/]+\/status\/|\/api\/gift\/claim\//)) {
        errorBus.notify(notFound);
      }
      return notFound;
    }
    if (error.response?.status === 410) {
      const gone = {
        data: error.response.data ?? null,
        message: error.response.data?.detail ?? '',
        code: 410,
      };
      // Suppress toast for onboarding — page renders used/expired-token state.
      const url = error.config?.url || '';
      if (!url.includes('/api/affiliate/onboarding/')) {
        errorBus.notify(gone);
      }
      return gone;
    }
    // 401/403 — only trigger session-expired on endpoints that REQUIRE auth for UX.
    // /api/user/info/ returns 403 for anonymous bootstrap — that's expected and silent.
    if (error.response?.status === 401 || error.response?.status === 403) {
      const code = error.response.status;
      const url = error.config?.url || '';

      // Endpoints where 401/403 means a real mid-session expiration (user was authed).
      // Bootstrap/read-only endpoints (user/info, game, draw) are intentionally excluded.
      const requiresAuthForUX = (
        url.includes('/api/order/') ||
        // /api/gift/claim/ is the PUBLIC (AllowAny) share-by-link endpoint — an
        // anonymous recipient hitting it is the normal case, never a session
        // expiry. Only the authenticated inbox/outbox routes qualify.
        (url.includes('/api/gift/') && !url.includes('/api/gift/claim/')) ||
        url.includes('/api/payment/') ||
        url.includes('/api/user/balance/') ||
        url.includes('/api/user/update-profile/') ||
        url.includes('/api/user/logout/')
        // /api/cart/ intentionally excluded: anonymous users have a local cart,
        // so a 401 on /api/cart/ is not a session expiration event.
      );

      if (requiresAuthForUX) {
        errorBus.notify({
          data: null,
          message: 'Sua sess\u00e3o expirou. Fa\u00e7a login novamente.',
          code,
        });
        window.dispatchEvent(new CustomEvent('lvs:session-expired'));
      }
      // Always return the error shape (non-requiresAuthForUX callers handle silently)
      return {
        data: null,
        message: requiresAuthForUX ? 'Sua sess\u00e3o expirou. Fa\u00e7a login novamente.' : '',
        code,
      };
    }
    try {
      if (error.response?.data) {
        if (error.response?.data.code) {
          //已知错误 交给hook处理
          let res = getTemplateError(error.response.data)
          if (!res) {
            for (const key in error.response?.data) {
              if (key !== "code") {
                if (typeof error.response.data[key] === "string") {
                  message = error.response.data[key];
                } else if (Array.isArray(error.response.data[key])) {
                  message = error.response.data[key].join(";");
                }
                break;
              }
            }
            if (message) {
              res = {
                message: message,
                code: error.response.data.code
              }
            }
          }
          if (!res) {
            // Structured error carried a code but no usable message anywhere —
            // never notify(false) (blank toast) or leak a bare code.
            res = {
              message: GENERIC_ERROR_MESSAGE,
              code: error.response.data.code
            }
          }
          if (res && res.code) {
            if ([1001, 1003, 1004, 999].includes(res.code)) {
              return res
            }
            // The public /presente/<token> claim page renders a dedicated screen
            // for every gift-domain code (already claimed / expired / retracted /
            // …) and field errors for under-age / bad phone. A global danger
            // snackbar on top of those would be redundant and alarming. Scoped to
            // the claim URL so the gift inbox/outbox and the normal registration
            // flow keep the toasts they have today.
            const giftUrl = error.config?.url || ''
            if (isGiftClaimUrl(giftUrl) && GIFT_CLAIM_CODES.includes(res.code)) {
              return res
            }
            // 9012 GIFT_LINK_QUOTA_INVALID is raised only at checkout (POST /api/order/).
            // The cart surfaces its own human copy (gift.linkQuotaBlocked), so suppress
            // the generic danger toast here — scoped to the order endpoint so the code
            // can never silently swallow an error anywhere else.
            if (
              res.code === GIFT_LINK_QUOTA_INVALID &&
              giftUrl.includes('/api/order/')
            ) {
              return res
            }
            if (["Enter a valid phone number."].includes(res.message)) {
              return res
            }
          }
          errorBus.notify(res)
          return res
        }
      }
      console.error("Error processing API response:", error);

    } catch (error) {
      console.error("Error processing API response:", error);

    }

    errorBus.notify({
      code: error.response?.data?.code || error.response?.status || 500,
      message: message || GENERIC_ERROR_MESSAGE,
    });
    return {
      code: error.response?.data?.code || error.response?.status || 500,
      message: message || GENERIC_ERROR_MESSAGE,
    };

  }
);

export default apiClient;